import { createBrowserRouter } from 'react-router';
import RootLayout from './layout/RootLayout';
import { AuthLayout } from './layout/AuthLayout';
import Dashboard from './pages/dashboard/Dashboard';
import ExploreProducts from './pages/explore-products/ExploreProducts';
import BuyerInventory from './pages/buyer-inventory/BuyerInventory';
import Cart from './pages/cart/Cart';
import Orders from './pages/orders/Orders';
import PriceNegotiations from './pages/price-negotiations/PriceNegotiations';
import MyQuotes from './pages/my-quotes/MyQuotes';
import MyRequestForQuotes from './pages/my-requests-for-quote/MyRequestForQuotes';
import { OrderView } from '~/pages/order-view/OrderView';
import { RfqView } from '~/pages/rfq-view/RfqView';
import { QuoteView } from '~/pages/quote-view/QuoteView';
import { Checkout } from './pages/checkout/Checkout';
import { Home } from './pages/home/Home';
import { SignInRedirect } from '~/components/auth/SignInRedirect.tsx';
import { RequireAuth } from '~/components/RequireAuth.tsx';
import { EditMyProduct } from './pages/my-products-edit/EditMyProduct';
import { MyProducts } from './pages/my-products/MyProducts';
import OrderBuilder from '~/pages/order-builder/OrderBuider.tsx';
import SignedOut from '~/components/auth/SignedOut.tsx';
import { Settings } from './pages/settings/Settings';
import Messages from './pages/messages/Messages';
import { ConfirmOrder } from '~/pages/confirm-order/ConfirmOrder';
import { CancelOrder } from '~/pages/cancel-order/CancelOrder';
import { DeclinePrice } from '~/pages/decline-price/DeclinePrice';
import { RespondToPriceNegotiation } from '~/pages/respond-to-price-negotiation/RespondToPriceNegotiation';
import { RespondToQuote } from '~/pages/respond-to-quote/RespondToQuote';
import { AnswerQuoteCounter } from '~/pages/answer-quote-counter/AnswerQuoteCounter';
import { Logistics } from './pages/logistics/Logistics';
import Users from '~/pages/users/Users';
import AdminOrganizations from './pages/admin-organizations/AdminOrganizations';
import AdminOrganizationDetail from './pages/admin-organizations/AdminOrganizationDetail';
import LinkedOrganizations from './pages/linked-organizations/LinkedOrganizations';
import LinkedOrganizationDetail from './pages/linked-organizations/LinkedOrganizationDetail';
import AdminInventoryClassification from './pages/admin-inventory-classification/AdminInventoryClassification';
import AdminJobOutbox from './pages/admin-job-outbox/AdminJobOutbox';
import AdminUfpInventory from './pages/admin-ufp-inventory/AdminUfpInventory';
import AdminProductImport from './pages/admin-product-import/AdminProductImport';
import AdminEmailDelivery from './pages/admin-email-delivery/AdminEmailDelivery';
import InventoryImport from './pages/inventory-import/InventoryImport';
import BuyLists from './pages/buy-lists/BuyLists';
import BuyListDetail from './pages/buy-lists/BuyListDetail';
import DocumentExtraction from './pages/document-extraction/DocumentExtraction';
import { RouteErrorBoundary } from './components/common/RouteErrorBoundary';
import { NotFound } from './pages/not-found/NotFound';

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'signin',
        element: <SignInRedirect />,
      },
      {
        path: 'signed-out',
        element: <SignedOut />,
      },
      {
        path: 'confirm-order',
        element: <ConfirmOrder />,
      },
      {
        path: 'cancel-order',
        element: <CancelOrder />,
      },
      {
        path: 'decline-price',
        element: <DeclinePrice />,
      },
      {
        path: 'respond-to-price-negotiation',
        element: <RespondToPriceNegotiation />,
      },
      {
        path: 'respond-to-quote',
        element: <RespondToQuote />,
      },
      {
        path: 'answer-quote-counter',
        element: <AnswerQuoteCounter />,
      },
    ],
  },

  // Authenticated routes

  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    element: (
      <RequireAuth>
        <AuthLayout />
      </RequireAuth>
    ),
    children: [
      {
        path: '/dashboard',
        element: <Dashboard />,
      },
      {
        path: '/buyer-inventory',
        element: <BuyerInventory />,
      },
      {
        path: '/buy-lists/view/:buyListId',
        element: <BuyListDetail />,
      },
      {
        path: '/buy-lists/*',
        element: <BuyLists />,
      },
      {
        path: '/request-for-quotes',
        element: <MyRequestForQuotes />,
      },
      {
        path: '/explore-products',
        element: <ExploreProducts />,
      },
      {
        path: '/cart',
        element: <Cart />,
      },
      {
        path: '/cart/:id/checkout/address/:addressId',
        element: <Checkout />,
      },
      {
        path: '/orders/view/:orderId',
        element: <OrderView />,
      },
      {
        path: '/rfq/view/:requestForQuoteId',
        element: <RfqView />,
      },
      {
        path: '/buyer-orders/*',
        element: <Orders perspective="BUYING" />,
      },
      {
        path: '/seller-orders/*',
        element: <Orders perspective="SELLING" />,
      },
      {
        path: '/buyer-price-negotiations/*',
        element: <PriceNegotiations perspective="BUYING" />,
      },
      {
        path: '/seller-price-negotiations/*',
        element: <PriceNegotiations perspective="SELLING" />,
      },
      {
        path: '/seller-quotes',
        element: <MyQuotes />,
      },
      {
        path: '/seller-quotes/view/:quoteId',
        element: <QuoteView />,
      },
      {
        path: '/my-products/*',
        element: <MyProducts />,
      },
      {
        path: '/my-products/import',
        element: <InventoryImport />,
      },
      {
        path: '/document-extraction',
        element: <DocumentExtraction />,
      },
      {
        path: '/my-products/:inventoryId/edit',
        element: <EditMyProduct />,
      },
      {
        path: '/order-builder/view',
        element: <OrderBuilder />,
      },
      {
        path: '/settings/*',
        element: <Settings />,
      },
      {
        path: '/buyer-messages',
        element: <Messages perspective="BUYING" />,
      },
      {
        path: '/seller-messages',
        element: <Messages perspective="SELLING" />,
      },
      {
        path: '/sea-freight/*',
        element: <Logistics />,
      },
      {
        path: '/users',
        element: <Users />,
      },
      {
        path: '/linked-organizations',
        element: <LinkedOrganizations />,
      },
      {
        path: '/linked-organizations/:organizationId',
        element: <LinkedOrganizationDetail />,
      },
      {
        path: '/admin/organizations',
        element: <AdminOrganizations />,
      },
      {
        path: '/admin/organizations/:organizationId',
        element: <AdminOrganizationDetail />,
      },
      {
        path: '/admin/inventory-classification',
        element: <AdminInventoryClassification />,
      },
      {
        path: '/admin/job-outbox',
        element: <AdminJobOutbox />,
      },
      {
        path: '/admin/email-delivery',
        element: <AdminEmailDelivery />,
      },
      {
        path: '/admin/ufp-inventory',
        element: <AdminUfpInventory />,
      },
      {
        path: '/admin/product-import',
        element: <AdminProductImport />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);
